import * as React from 'react'
import { Button } from '@/ui/button'
import { ServerModel } from 'app/models/serverModel'
import { MessageModel } from 'app/models/messageModel'

import { MessageType } from 'app/constants/messageType'

export interface ServerUserItem {
  userId: number
  login: string
  firstName: string
  surname: string
}

export type ServerUsersModalDispatchProps = {
  showMessage: (message: MessageModel) => void
  getServerUsers: (serverId: string) => Promise<ServerUserItem[]>
  setServerUsers: (serverId: string, userIds: string[]) => Promise<void>
  getAllUsers: () => Promise<any>
}

export type ServerUsersModalOwnProps = {
  server: ServerModel
  translate: (key: string) => string
  onClose: () => void
}

type Props = ServerUsersModalDispatchProps & ServerUsersModalOwnProps

type State = {
  assignedUsers: ServerUserItem[]
  allUsers: ServerUserItem[]
  searchValue: string
  showDropdown: boolean
  usersLoading: boolean
  usersSaving: boolean
}

class ServerUsersModal extends React.Component<Props, State> {
  private dropdownRef = React.createRef<HTMLDivElement>()

  constructor(props: Props) {
    super(props)
    this.state = {
      assignedUsers: [],
      allUsers: [],
      searchValue: '',
      showDropdown: false,
      usersLoading: false,
      usersSaving: false,
    }
  }

  async componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside)
    if (this.props.server?.id) {
      this.setState({ usersLoading: true })
      try {
        const [serverUsers, allUsersResponse] = await Promise.all([
          this.props.getServerUsers(String(this.props.server.id)),
          this.props.getAllUsers(),
        ])
        const allUsers: ServerUserItem[] = (allUsersResponse?.content?.data || []).map((u: any) => ({
          userId: u.id,
          login: u.login,
          firstName: u.firstname || u.firstName || '',
          surname: u.surname || '',
        }))
        this.setState({ assignedUsers: serverUsers || [], allUsers })
      } finally {
        this.setState({ usersLoading: false })
      }
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside)
  }

  handleClickOutside = (e: MouseEvent) => {
    if (this.dropdownRef.current && !this.dropdownRef.current.contains(e.target as Node)) {
      this.setState({ showDropdown: false })
    }
  }

  removeUser = (userId: number) => {
    this.setState((prev) => ({
      assignedUsers: prev.assignedUsers.filter((u) => u.userId !== userId),
    }))
  }

  addUser = (user: ServerUserItem) => {
    if (!this.state.assignedUsers.some((u) => u.userId === user.userId)) {
      this.setState((prev) => ({
        assignedUsers: [...prev.assignedUsers, user],
        searchValue: '',
        showDropdown: false,
      }))
    }
  }

  saveUsers = async () => {
    const serverId = String(this.props.server?.id)
    if (!serverId) return
    this.setState({ usersSaving: true })
    try {
      await this.props.setServerUsers(serverId, this.state.assignedUsers.map((u) => String(u.userId)))
      this.props.showMessage({
        text: this.props.translate('administrationPanel-servers.users-saved'),
        details: '',
        type: MessageType.INFO,
      })
    } catch {
      this.props.showMessage({
        text: this.props.translate('administrationPanel-servers.users-save-error'),
        details: '',
        type: MessageType.ERROR,
      })
    } finally {
      this.setState({ usersSaving: false })
    }
  }

  render() {
    const { translate, server } = this.props
    const { searchValue, showDropdown, allUsers, assignedUsers, usersLoading, usersSaving } = this.state

    const filteredUsers = allUsers.filter((u) => {
      if (assignedUsers.some((a) => a.userId === u.userId)) return false
      const q = searchValue.toLowerCase()
      return (
        u.login?.toLowerCase().includes(q) ||
        u.firstName?.toLowerCase().includes(q) ||
        u.surname?.toLowerCase().includes(q)
      )
    }).slice(0, 10)

    return (
      <div style={{ padding: '8px 0' }}>
        <p style={{ color: '#6b7280', fontSize: 13, marginBottom: 16 }}>
          {server?.name} — {server?.address}
        </p>

        {usersLoading ? (
          <p style={{ fontSize: 13, color: '#6b7280' }}>{translate('administrationPanel-servers.users-loading')}</p>
        ) : (
          <>
            {assignedUsers.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                {assignedUsers.map((u) => (
                  <div
                    key={u.userId}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      background: '#eff6ff',
                      border: '1px solid #bfdbfe',
                      borderRadius: 20,
                      padding: '4px 10px',
                      fontSize: 13,
                      color: '#1d4ed8',
                    }}
                  >
                    <span>{u.login}</span>
                    <button
                      type="button"
                      onClick={() => this.removeUser(u.userId)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 1, color: '#1d4ed8', fontSize: 14 }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
            {assignedUsers.length === 0 && (
              <p style={{ fontSize: 13, color: '#9ca3af', marginBottom: 12 }}>
                {translate('administrationPanel-servers.users-none')}
              </p>
            )}

            <div ref={this.dropdownRef} style={{ position: 'relative', maxWidth: 340, marginBottom: 16 }}>
              <input
                type="text"
                placeholder={translate('administrationPanel-servers.users-search-placeholder')}
                value={searchValue}
                onChange={(e) => this.setState({ searchValue: e.target.value, showDropdown: true })}
                onFocus={() => this.setState({ showDropdown: true })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                  fontSize: 13,
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
              {showDropdown && searchValue.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  background: '#fff',
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  zIndex: 1000,
                  maxHeight: 200,
                  overflowY: 'auto',
                }}>
                  {filteredUsers.length === 0 ? (
                    <div style={{ padding: '8px 12px', fontSize: 13, color: '#9ca3af' }}>
                      {translate('administrationPanel-servers.users-no-results')}
                    </div>
                  ) : (
                    filteredUsers.map((u) => (
                      <div
                        key={u.userId}
                        onClick={() => this.addUser(u)}
                        style={{ padding: '8px 12px', fontSize: 13, cursor: 'pointer', borderBottom: '1px solid #f3f4f6', color: '#111827' }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = '#f3f4f6')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                      >
                        <div style={{ fontWeight: 500 }}>{u.login}</div>
                        {(u.firstName || u.surname) && (
                          <div style={{ fontSize: 12, color: '#6b7280' }}>{u.firstName} {u.surname}</div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                variant="primary"
                size="sm"
                disabled={usersSaving}
                onClick={this.saveUsers}
              >
                {translate('administrationPanel-servers.users-save')}
              </Button>
              <Button
                variant="primary"
                mode="outline"
                size="sm"
                onClick={this.props.onClose}
              >
                {translate('operation.cancel')}
              </Button>
            </div>
          </>
        )}
      </div>
    )
  }
}

export default ServerUsersModal
